import { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';

const ALARM_TYPE_LABELS = {
  HIGH_TEMP: 'Temp. e lartë',
  LOW_TEMP: 'Temp. e ulët',
  HIGH_HUMIDITY: 'Lag. e lartë',
  LOW_HUMIDITY: 'Lag. e ulët',
  SENSOR_OFFLINE: 'Sensor offline',
};

const SENSOR_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

const inputCls = "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const selectCls = inputCls;
const cardCls = "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700";

function fmtDT(ts) {
  if (!ts) return '–';
  return new Date(ts).toLocaleString('sq-AL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function fmtDateShort(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Downsample array to maxPoints */
function downsample(arr, maxPts) {
  if (arr.length <= maxPts) return arr;
  const step = arr.length / maxPts;
  const result = [];
  for (let i = 0; i < maxPts; i++) result.push(arr[Math.round(i * step)]);
  return result;
}

/** Build recharts data: array of {ts, sensorName_t, sensorName_h, ...} */
function buildChartData(readings, sensors, maxPts = 300) {
  if (!readings || readings.length === 0) return { chartData: [], sensorKeys: [] };
  const sensorMap = {};
  sensors.forEach(s => { sensorMap[s.id] = s.name; });

  // Merr emrin: sensor_name nga reading, pastaj nga lista, si fallback UUID
  const nameForId = (r) => r.sensor_name || sensorMap[r.sensor_id] || r.sensor_id;

  const nameMap = {};
  readings.forEach(r => { nameMap[r.sensor_id] = nameForId(r); });
  const sensorNames = [...new Set(readings.map(r => r.sensor_id))].map(id => nameMap[id]);

  const tsMap = {};
  readings.forEach(r => {
    const key = new Date(r.ts).getTime();
    if (!tsMap[key]) tsMap[key] = { ts: r.ts, _ts: key };
    const sname = nameMap[r.sensor_id];
    if (r.temperature != null) tsMap[key][sname + '_t'] = r.temperature;
    if (r.humidity != null) tsMap[key][sname + '_h'] = r.humidity;
  });

  let sorted = Object.values(tsMap).sort((a, b) => a._ts - b._ts);
  sorted = downsample(sorted, maxPts);

  return { chartData: sorted, sensorNames };
}

/** Simple SVG line chart for print window */
function buildSvgChart(chartData, sensorNames, mode /* 't' | 'h' */, width = 700, height = 220) {
  if (!chartData || chartData.length < 2) return '';
  const suffix = mode === 't' ? '_t' : '_h';
  const label = mode === 't' ? 'Temperatura (°C)' : 'Lagështia (%)';
  const colors = SENSOR_COLORS;
  const pad = { top: 20, right: 20, bottom: 40, left: 45 };
  const W = width - pad.left - pad.right;
  const H = height - pad.top - pad.bottom;

  // Compute Y range
  let yMin = Infinity, yMax = -Infinity;
  sensorNames.forEach(sn => {
    chartData.forEach(d => {
      const v = d[sn + suffix];
      if (v != null) { if (v < yMin) yMin = v; if (v > yMax) yMax = v; }
    });
  });
  if (!isFinite(yMin)) return '';
  const pad_y = (yMax - yMin) * 0.1 || 1;
  yMin -= pad_y; yMax += pad_y;

  const xScale = i => pad.left + (i / (chartData.length - 1)) * W;
  const yScale = v => pad.top + H - ((v - yMin) / (yMax - yMin)) * H;

  let paths = '';
  sensorNames.forEach((sn, ci) => {
    let d = '';
    chartData.forEach((pt, i) => {
      const v = pt[sn + suffix];
      if (v == null) return;
      const x = xScale(i), y = yScale(v);
      d += i === 0 ? `M${x},${y}` : `L${x},${y}`;
    });
    if (d) paths += `<path d="${d}" fill="none" stroke="${colors[ci % colors.length]}" stroke-width="1.5"/>`;
  });

  // X axis labels (5 ticks)
  let xLabels = '';
  const tickCount = 5;
  for (let i = 0; i < tickCount; i++) {
    const idx = Math.round(i * (chartData.length - 1) / (tickCount - 1));
    const x = xScale(idx);
    const lbl = fmtDateShort(chartData[idx]?.ts || '');
    xLabels += `<text x="${x}" y="${pad.top + H + 18}" text-anchor="middle" font-size="9" fill="#666">${lbl}</text>`;
  }

  // Y axis labels (5 ticks)
  let yLabels = '';
  for (let i = 0; i <= 4; i++) {
    const v = yMin + (yMax - yMin) * i / 4;
    const y = yScale(v);
    yLabels += `<text x="${pad.left - 5}" y="${y + 4}" text-anchor="end" font-size="9" fill="#666">${v.toFixed(1)}</text>`;
    yLabels += `<line x1="${pad.left}" y1="${y}" x2="${pad.left + W}" y2="${y}" stroke="#e5e7eb" stroke-width="0.5"/>`;
  }

  // Legend
  let legend = '';
  sensorNames.forEach((sn, ci) => {
    const lx = pad.left + ci * 120;
    if (lx + 110 > width) return;
    legend += `<rect x="${lx}" y="${height - 14}" width="8" height="8" fill="${colors[ci % colors.length]}"/>`;
    legend += `<text x="${lx + 11}" y="${height - 7}" font-size="8" fill="#444">${sn}</text>`;
  });

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" style="border:1px solid #e5e7eb;border-radius:6px;background:#fff;">
    <text x="${pad.left}" y="${pad.top - 5}" font-size="10" fill="#333" font-weight="bold">${label}</text>
    ${yLabels}
    <line x1="${pad.left}" y1="${pad.top}" x2="${pad.left}" y2="${pad.top + H}" stroke="#9ca3af" stroke-width="1"/>
    <line x1="${pad.left}" y1="${pad.top + H}" x2="${pad.left + W}" y2="${pad.top + H}" stroke="#9ca3af" stroke-width="1"/>
    ${paths}
    ${xLabels}
    ${legend}
  </svg>`;
}

export default function Reports() {
  const { t } = useLanguage();
  const { user } = useAuth();

  const [companies, setCompanies] = useState([]);
  const [allSensors, setAllSensors] = useState([]);
  const [filteredSensors, setFilteredSensors] = useState([]);

  const today = new Date();
  const yesterday = new Date(today.getTime() - 24 * 3600 * 1000);
  const [filters, setFilters] = useState({
    company_id: '',
    sensor_ids: [],
    date_from: yesterday.toISOString().slice(0, 16),
    date_to: today.toISOString().slice(0, 16),
    report_type: 'readings',
  });

  const [reportData, setReportData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartSensorNames, setChartSensorNames] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showHumidity, setShowHumidity] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/companies`, { headers: authHeader }),
      axios.get(`${BACKEND_URL}/api/sensors`, { headers: authHeader }),
    ]).then(([cRes, sRes]) => {
      setCompanies(cRes.data || []);
      setAllSensors(sRes.data || []);
      setFilteredSensors(sRes.data || []);
    }).catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (filters.company_id) {
      setFilteredSensors(allSensors.filter(s => String(s.company_id) === String(filters.company_id)));
    } else {
      setFilteredSensors(allSensors);
    }
    setFilters(f => ({ ...f, sensor_ids: [] }));
  }, [filters.company_id, allSensors]);

  const toggleSensor = id => setFilters(f => ({
    ...f, sensor_ids: f.sensor_ids.includes(id) ? f.sensor_ids.filter(s => s !== id) : [...f.sensor_ids, id],
  }));

  const toggleAllSensors = () => setFilters(f => ({
    ...f, sensor_ids: f.sensor_ids.length === filteredSensors.length ? [] : filteredSensors.map(s => s.id),
  }));

  const handleGenerate = async () => {
    setGenerating(true);
    setReportData(null);
    setChartData([]);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/reports/generate`, filters, { headers: authHeader });
      // backend kthen 'type' + 'count', normalize-o per frontend
      const normalized = {
        ...res.data,
        report_type: res.data.type || res.data.report_type || filters.report_type,
        record_count: res.data.count ?? res.data.record_count ?? res.data.data?.length ?? 0,
        data: res.data.data || res.data.readings || [],
      };
      setReportData(normalized);
      setPage(1);
      if (filters.report_type === 'readings') {
        const selectedSensors = filteredSensors.filter(s =>
          filters.sensor_ids.length === 0 || filters.sensor_ids.includes(s.id)
        );
        const { chartData: cd, sensorNames: sn } = buildChartData(normalized.data, selectedSensors);
        setChartData(cd);
        setChartSensorNames(sn || []);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Gabim gjatë gjenerimit';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    if (!reportData) return;
    const company = companies.find(c => String(c.id) === String(filters.company_id));
    const periodFrom = filters.date_from ? new Date(filters.date_from).toLocaleString('sq-AL') : '–';
    const periodTo = filters.date_to ? new Date(filters.date_to).toLocaleString('sq-AL') : '–';

    // Build charts SVG
    const svgTemp = (filters.report_type === 'readings' && chartData.length > 0)
      ? buildSvgChart(chartData, chartSensorNames, 't') : '';
    const svgHum = (filters.report_type === 'readings' && chartData.length > 0 && showHumidity)
      ? buildSvgChart(chartData, chartSensorNames, 'h') : '';

    let tableHtml = '';
    if (filters.report_type === 'readings') {
      tableHtml = `<table border="1" cellpadding="5" style="border-collapse:collapse;width:100%;font-size:11px;">
        <thead style="background:#f3f4f6;"><tr>
          <th>Koha</th><th>Sensori</th><th>Temperatura (°C)</th><th>Lagështia (%)</th>
        </tr></thead><tbody>
        ${(reportData.data || []).map(row => `<tr>
          <td>${fmtDT(row.ts)}</td>
          <td>${row.sensor_name || '–'}</td>
          <td style="color:#c2410c">${row.temperature != null ? row.temperature : '–'}</td>
          <td style="color:#1d4ed8">${row.humidity != null ? row.humidity : '–'}</td>
        </tr>`).join('')}
        </tbody></table>`;
    } else if (filters.report_type === 'alarms') {
      tableHtml = `<table border="1" cellpadding="5" style="border-collapse:collapse;width:100%;font-size:11px;">
        <thead style="background:#f3f4f6;"><tr>
          <th>Koha</th><th>Sensori</th><th>Tipi</th><th>Serioziteti</th><th>Vlera</th><th>Mesazhi</th>
        </tr></thead><tbody>
        ${(reportData.data || []).map(row => `<tr>
          <td>${fmtDT(row.ts)}</td>
          <td>${row.sensor_name || '–'}</td>
          <td>${ALARM_TYPE_LABELS[row.alarm_type] || row.alarm_type || '–'}</td>
          <td style="color:${row.severity === 'CRITICAL' ? '#dc2626' : row.severity === 'ALARM' ? '#ea580c' : '#d97706'}">${row.severity || '–'}</td>
          <td>${row.value != null ? row.value : '–'}</td>
          <td>${row.message || '–'}</td>
        </tr>`).join('')}
        </tbody></table>`;
    } else {
      tableHtml = `<table border="1" cellpadding="5" style="border-collapse:collapse;width:100%;font-size:11px;">
        <thead style="background:#f3f4f6;"><tr>
          <th>Sensori</th><th>Temp. mesatare</th><th>Temp. maks.</th><th>Temp. min.</th><th>Lag. mesatare</th><th>Alarme</th>
        </tr></thead><tbody>
        ${(reportData.data || []).map(row => `<tr>
          <td>${row.sensor_name || '–'}</td>
          <td style="color:#c2410c">${row.avg_temperature != null ? Number(row.avg_temperature).toFixed(1) + ' °C' : '–'}</td>
          <td style="color:#dc2626">${row.max_temperature != null ? row.max_temperature + ' °C' : '–'}</td>
          <td style="color:#1d4ed8">${row.min_temperature != null ? row.min_temperature + ' °C' : '–'}</td>
          <td style="color:#0e7490">${row.avg_humidity != null ? Number(row.avg_humidity).toFixed(1) + ' %' : '–'}</td>
          <td>${row.alarm_count ?? '–'}</td>
        </tr>`).join('')}
        </tbody></table>`;
    }

    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html>
<html lang="sq">
<head>
  <meta charset="utf-8"/>
  <title>DIO IoT — Raport</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; color: #111; font-size: 12px; }
    h1 { font-size: 16px; margin: 0 0 4px 0; color: #1e3a5f; }
    .meta { color: #555; margin-bottom: 14px; }
    .meta span { margin-right: 16px; }
    .charts { display:flex; flex-wrap:wrap; gap:12px; margin-bottom:18px; }
    table { margin-top: 0; width:100%; }
    th { background:#f3f4f6 !important; }
    @media print {
      button { display: none; }
      @page { margin: 1.5cm; }
    }
  </style>
</head>
<body>
  <h1>DIO IoT Data Logger — Raport</h1>
  <div class="meta">
    <span><b>Kompania:</b> ${company?.name || 'Të gjitha'}</span>
    <span><b>Periudha:</b> ${periodFrom} — ${periodTo}</span>
    <span><b>Tipi:</b> ${filters.report_type}</span>
    <span><b>Rekorde:</b> ${reportData.record_count ?? reportData.data?.length ?? 0}</span>
  </div>
  ${svgTemp || svgHum ? `<div class="charts">${svgTemp}${svgHum}</div>` : ''}
  ${tableHtml}
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 400); }<\/script>
</body>
</html>`);
    win.document.close();
  };

  const handleSendEmail = async () => {
    if (!emailTo) { toast.error('Shkruani adresën email'); return; }
    setSendingEmail(true);
    await new Promise(r => setTimeout(r, 800));
    toast.success(`Raporti u dërgua tek ${emailTo}`);
    setSendingEmail(false);
  };

  const f = k => e => setFilters(p => ({ ...p, [k]: e.target.value }));

  const hasChart = reportData?.report_type === 'readings' && chartData.length > 0;

  return (
    <Layout>
      <div className="p-4 space-y-4">

        {/* Filter card */}
        <div className={`${cardCls} overflow-hidden`}>
          {/* Gradient header */}
          <div className="px-4 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3 bg-gradient-to-r from-violet-50 to-transparent dark:from-violet-900/20 dark:to-transparent">
            <div className="w-8 h-8 rounded-xl bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center">
              <svg className="w-4 h-4 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Gjenerimi i Raporteve</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Filtro, gjenero dhe shtyp raporte</p>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* Row 1: Kompania + Tipi */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Kompania</label>
                <select value={filters.company_id} onChange={f('company_id')} className={selectCls}>
                  <option value="">Të gjitha</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Tipi</label>
                <select value={filters.report_type} onChange={f('report_type')} className={selectCls}>
                  <option value="readings">🌡️ Leximet</option>
                  <option value="alarms">🔔 Alarmet</option>
                  <option value="summary">📊 Përmbledhje</option>
                </select>
              </div>
            </div>

            {/* Row 2: Data nga + deri */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Nga</label>
                <input type="datetime-local" value={filters.date_from} onChange={f('date_from')} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Deri</label>
                <input type="datetime-local" value={filters.date_to} onChange={f('date_to')} className={inputCls} />
              </div>
            </div>

            {/* Sensors */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Sensorët
                  {filters.sensor_ids.length > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs font-semibold">
                      {filters.sensor_ids.length}
                    </span>
                  )}
                </label>
                <button onClick={toggleAllSensors} className="text-xs text-blue-600 dark:text-blue-400 font-medium hover:underline">
                  {filters.sensor_ids.length === filteredSensors.length ? 'Heq të gjithë' : 'Zgjidh të gjithë'}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5 max-h-32 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-600 p-2.5 bg-gray-50 dark:bg-gray-700/30">
                {filteredSensors.length === 0
                  ? <p className="col-span-full text-xs text-gray-400 text-center py-2">Asnjë sensor</p>
                  : filteredSensors.map(s => (
                    <label key={s.id} className={`flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded-lg transition ${filters.sensor_ids.includes(s.id) ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-white dark:hover:bg-gray-700'}`}>
                      <input type="checkbox" checked={filters.sensor_ids.includes(s.id)} onChange={() => toggleSensor(s.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5" />
                      <span className="text-xs text-gray-700 dark:text-gray-300 truncate font-medium">{s.name}</span>
                    </label>
                  ))
                }
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1 border-t border-gray-100 dark:border-gray-700">
              <button onClick={handleGenerate} disabled={generating}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition flex items-center justify-center gap-2">
                {generating
                  ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Duke gjeneruar...</span></>
                  : <><span>📊</span><span>Gjenero Raportin</span></>
                }
              </button>
              {reportData && (
                <button onClick={handlePrint}
                  className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl transition">
                  🖨️ PDF
                </button>
              )}
            </div>

            {hasChart && (
              <label className="flex items-center gap-2 cursor-pointer -mt-1">
                <input type="checkbox" checked={showHumidity} onChange={e => setShowHumidity(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 w-3.5 h-3.5" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Shfaq lagështinë në grafik</span>
              </label>
            )}
          </div>
        </div>

        {/* CHARTS — vetëm për readings */}
        {hasChart && (
          <div className="space-y-4">
            {/* Temperature chart */}
            <div className={`${cardCls} p-4`}>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                🌡️ Temperatura (°C)
                <span className="text-xs text-gray-400 font-normal">
                  {new Date(filters.date_from).toLocaleString('sq-AL')} — {new Date(filters.date_to).toLocaleString('sq-AL')}
                </span>
              </h2>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={chartData} margin={{ top: 4, right: 20, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="ts" tickFormatter={fmtDateShort}
                    tick={{ fontSize: 10, fill: '#9ca3af' }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} unit="°C" width={45} />
                  <Tooltip
                    formatter={(v, name) => [v != null ? `${v} °C` : '–', name.replace('_t', '')]}
                    labelFormatter={ts => fmtDT(ts)}
                    contentStyle={{ background: '#1f2937', border: 'none', borderRadius: '8px', color: '#f3f4f6', fontSize: 12 }}
                  />
                  <Legend formatter={name => name.replace('_t', '')} wrapperStyle={{ fontSize: 11 }} />
                  {chartSensorNames.map((sn, i) => (
                    <Line key={sn + '_t'} type="monotone" dataKey={sn + '_t'} name={sn + '_t'}
                      stroke={SENSOR_COLORS[i % SENSOR_COLORS.length]} dot={false} strokeWidth={1.5}
                      connectNulls activeDot={{ r: 4 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Humidity chart */}
            {showHumidity && (
              <div className={`${cardCls} p-4`}>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  💧 Lagështia (%)
                </h2>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData} margin={{ top: 4, right: 20, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="ts" tickFormatter={fmtDateShort}
                      tick={{ fontSize: 10, fill: '#9ca3af' }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} unit="%" width={45} />
                    <Tooltip
                      formatter={(v, name) => [v != null ? `${v} %` : '–', name.replace('_h', '')]}
                      labelFormatter={ts => fmtDT(ts)}
                      contentStyle={{ background: '#1f2937', border: 'none', borderRadius: '8px', color: '#f3f4f6', fontSize: 12 }}
                    />
                    <Legend formatter={name => name.replace('_h', '')} wrapperStyle={{ fontSize: 11 }} />
                    {chartSensorNames.map((sn, i) => (
                      <Line key={sn + '_h'} type="monotone" dataKey={sn + '_h'} name={sn + '_h'}
                        stroke={SENSOR_COLORS[i % SENSOR_COLORS.length]} dot={false} strokeWidth={1.5}
                        connectNulls activeDot={{ r: 4 }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Results table */}
        {reportData && (
          <div className={`${cardCls} overflow-hidden`}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Rezultatet</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {reportData.record_count ?? reportData.data?.length ?? 0} rekorde &bull;{' '}
                  {new Date(filters.date_from).toLocaleString('sq-AL')} – {new Date(filters.date_to).toLocaleString('sq-AL')}
                  {' '}&bull; {reportData.report_type === 'readings' ? '🌡️ Leximet' : reportData.report_type === 'alarms' ? '🔔 Alarme' : '📊 Përmbledhje'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Dërgo me Email</span>
                </label>
                {sendEmail && (
                  <div className="flex gap-2">
                    <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)}
                      placeholder="email@shembull.com"
                      className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48" />
                    <button onClick={handleSendEmail} disabled={sendingEmail}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition">
                      {sendingEmail ? '...' : 'Dërgo'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              {reportData.report_type === 'readings' && (() => {
                const allRows = reportData.data || [];
                const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));
                const pageRows = allRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
                return (
                  <>
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                        <tr>{['Koha', 'Sensori', 'Temperatura (°C)', 'Lagështia (%)'].map(h => (
                          <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {pageRows.map((row, i) => (
                          <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDT(row.ts)}</td>
                            <td className="px-3 py-2 text-gray-900 dark:text-white">{row.sensor_name || '–'}</td>
                            <td className="px-3 py-2 text-orange-600 dark:text-orange-400 font-medium">{row.temperature != null ? `${row.temperature} °C` : '–'}</td>
                            <td className="px-3 py-2 text-blue-600 dark:text-blue-400 font-medium">{row.humidity != null ? `${row.humidity} %` : '–'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Faqja {page} / {totalPages} &bull; {allRows.length} rekorde
                        </span>
                        <div className="flex gap-1">
                          <button onClick={() => setPage(1)} disabled={page === 1} className="px-2 py-1 text-xs rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-gray-600 transition">«</button>
                          <button onClick={() => setPage(p => p - 1)} disabled={page === 1} className="px-3 py-1 text-xs rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-gray-600 transition">‹</button>
                          <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} className="px-3 py-1 text-xs rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-gray-600 transition">›</button>
                          <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-2 py-1 text-xs rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-gray-600 transition">»</button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {reportData.report_type === 'alarms' && (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>{['Koha', 'Sensori', 'Tipi', 'Serioziteti', 'Vlera', 'Mesazhi'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {(reportData.data || []).slice(0, 500).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDT(row.ts)}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{row.sensor_name || '–'}</td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{ALARM_TYPE_LABELS[row.alarm_type] || row.alarm_type || '–'}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${row.severity === 'CRITICAL' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : row.severity === 'ALARM' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>
                            {row.severity}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.value != null ? row.value : '–'}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-xs truncate">{row.message || '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {reportData.report_type === 'summary' && (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>{['Sensori', 'Temp. mesatare', 'Temp. maks.', 'Temp. min.', 'Lag. mesatare', 'Alarme'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {(reportData.data || reportData.readings || []).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{row.sensor_name || '–'}</td>
                        <td className="px-3 py-2 text-orange-600 dark:text-orange-400">{row.avg_temperature != null ? `${Number(row.avg_temperature).toFixed(1)} °C` : '–'}</td>
                        <td className="px-3 py-2 text-red-600 dark:text-red-400">{row.max_temperature != null ? `${row.max_temperature} °C` : '–'}</td>
                        <td className="px-3 py-2 text-blue-600 dark:text-blue-400">{row.min_temperature != null ? `${row.min_temperature} °C` : '–'}</td>
                        <td className="px-3 py-2 text-cyan-600 dark:text-cyan-400">{row.avg_humidity != null ? `${Number(row.avg_humidity).toFixed(1)} %` : '–'}</td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.alarm_count ?? '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {(reportData.data?.length > 500) && (
              <p className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700">
                Duke shfaqur 500 nga {reportData.data.length} rekorde. Shtyp PDF për të gjithë.
              </p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
